import { ShieldCheck, Zap, Gem, Headset } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

const features = [
  { icon: ShieldCheck, k: "f1" },
  { icon: Zap, k: "f2" },
  { icon: Gem, k: "f3" },
  { icon: Headset, k: "f4" },
];

export const About = () => {
  const { t } = useLang();
  return (
    <section id="about" className="relative py-20 sm:py-28 border-t border-white/[0.05]" data-testid="about-section">
      <div className="glow-orb w-[500px] h-[500px] bg-[#00f2fe]/[0.06] top-1/3 -right-60" />
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <Reveal>
              <div className="eyebrow mb-6">{t.about.eyebrow}</div>
              <h2 className="font-display font-extrabold tracking-[-0.02em] text-2xl sm:text-3xl lg:text-4xl text-[#f4f2ec] leading-[1.1]" data-testid="about-title">
                {t.about.title1}
                <br />
                <span className="text-gold-gradient">{t.about.title2}</span>
              </h2>
              <p className="mt-7 text-[#8a8a8a] text-sm leading-relaxed max-w-md">{t.about.desc}</p>
            </Reveal>
            <Reveal delay={0.2} className="mt-10 bento p-7">
              <p className="font-display text-base sm:text-lg text-[#e8eef0] leading-snug">{t.about.quote}</p>
              <div className="mt-5 flex items-center gap-3">
                <span className="w-8 h-px bg-[#00f2fe]" />
                <span className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-[#00f2fe]">{t.about.quoteBy}</span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5" data-testid="about-grid">
            <Reveal className="sm:col-span-2 bento overflow-hidden min-h-[260px]">
              <img
                src="https://images.pexels.com/photos/28506813/pexels-photo-28506813.jpeg?auto=compress&cs=tinysrgb&w=1400"
                alt="rojdijital headquarters"
                className="absolute inset-0 w-full h-full object-cover opacity-50 saturate-[0.6]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b] via-[#0b0b0b]/70 to-transparent" />
              <div className="relative p-8 grid grid-cols-2 sm:grid-cols-4 gap-6 h-full items-end">
                {["s1", "s2", "s3", "s4"].map((s) => (
                  <div key={s} data-testid={`about-stat-${s}`}>
                    <div className="font-display font-extrabold text-xl lg:text-2xl text-gold-gradient whitespace-nowrap">{t.about[s]}</div>
                    <div className="font-mono text-[0.6rem] tracking-[0.18em] uppercase text-[#8a8a8a] mt-1 whitespace-nowrap">{t.about[`${s}d`]}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            {features.map(({ icon: Icon, k }, i) => (
              <Reveal key={k} delay={0.08 * i} className="bento p-7" data-testid={`about-feature-${k}`}>
                <div className="icon-tile mb-6">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-display font-bold text-base text-[#f4f2ec]">{t.about[`${k}t`]}</h3>
                <p className="mt-2 text-sm text-[#8a8a8a] leading-relaxed">{t.about[`${k}d`]}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
